/**
 * Holds Clarity and Crisp off the network while an upload is starting.
 *
 * The window that matters is "file chosen → first part confirmed by storage":
 * nothing has landed yet, so a third-party script load competing for a thin
 * cellular uplink delays the one confirmation that proves the upload works.
 * Once the first part is confirmed the engine has a real measurement and the
 * part loop handles contention like any other dip.
 *
 * The first *confirmed* part is used rather than upload_first_byte: the first
 * byte fires as soon as the socket starts filling, so loading then would
 * still compete with part 1.
 *
 * The hold is capped at QUIET_CAP_MS from attempt start. A stuck upload is
 * exactly when the support chat and session replay are most useful, so a
 * first part that never lands must not keep them away. A support tap releases
 * the hold for the rest of the page load.
 *
 * Attempt identity and start time come from telemetry's attempt; this module
 * only records which attempt has a confirmed first part. GA is not gated.
 */
import { getAttempt } from '../analytics/telemetry';

const QUIET_CAP_MS = 30_000;

let firstPartConfirmedFor: string | null = null;
let releasedByUser = false;
const waiters = new Set<() => void>();

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** ms left in the hold for the current attempt, or 0 when nothing is held. */
const msLeftInWindow = (): number => {
  if (releasedByUser) return 0;
  const attempt = getAttempt();
  if (!attempt || attempt.completed || firstPartConfirmedFor === attempt.id) return 0;
  return Math.max(0, attempt.startedAt + QUIET_CAP_MS - now());
};

const flush = (): void => {
  Array.from(waiters).forEach((done) => done());
};

/** Resolves immediately unless an upload is between file chosen and first
 *  part confirmed; then when that part lands, or at the cap. */
export const whenUploadQuiet = (): Promise<void> =>
  new Promise((resolve) => {
    const msLeft = msLeftInWindow();
    if (msLeft === 0) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      waiters.delete(done);
      resolve();
    };
    const timer = setTimeout(done, msLeft);
    waiters.add(done);
  });

/** Called by the engine on every confirmed part; only the first per attempt
 *  changes anything. */
export const markPartConfirmed = (): void => {
  const id = getAttempt()?.id ?? null;
  if (id === null || firstPartConfirmedFor === id) return;
  firstPartConfirmedFor = id;
  flush();
};

/** The user asked for help: stop holding for the rest of the page load. */
export const releaseUploadQuiet = (): void => {
  if (releasedByUser) return;
  releasedByUser = true;
  flush();
};
